import { useState } from "react"
import { GetApp } from "../../App"
import { localStorageId } from "../../App/LocalStorage"
import { Session } from "../../App/Session"
import { logger } from "../../App/Logger"
import { Block, BlockContainer, BlockField, BlockRow } from "../../Components/Block"
import { Loading } from "../../Components/Loading"
import { Overlay, OverlayTitle } from "../../Components/Overlay"
import { AppRoutes, GetAppView } from "../AppView"
import { AppOverlayContent } from "../AppOverlayContentView"
import { ShipSelectView } from "../ShipSelectView"
import { AccountSettingsShipsViewProps } from "./Params"
import { ShipSelectViewData } from "../ShipSelect/Context"
import { invalidateCompanionConnect } from "../ShipSelect/Connect"

export function AccountSettingsShipsView({ accountShips, sharedShips, nearbyShipIds, ships, cancelEdit, companionReconnect, companionShipId: companionShipIdOverride, companionDisconnectState }: AccountSettingsShipsViewProps) {
  const app = GetApp();
  const { navigateApp, setAppOverlay, resetAppOverlay, membership, ownShipId } = GetAppView();
  const t = app.getT("AccountSettingsView");
  const connectT = app.getT("AppOverlayContentView");
  const [disconnected, setDisconnected] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectFailed, setDisconnectFailed] = useState(false);
  const cleanupPending = disconnecting || companionDisconnectState === "pending";
  const cleanupFailed = !cleanupPending && (disconnectFailed || companionDisconnectState === "failed");
  const storedCompanionShipId = companionShipIdOverride ?? localStorageId("logbook-companion");
  const companionShipId = !disconnected && !app.cloudSession() && !app.localSession() && storedCompanionShipId !== ownShipId
    ? storedCompanionShipId : undefined;

  const user = app.user || { accountId: undefined, email: "", name: "", shortname: "" };

  const shipSelected = !app.cloudSession() && !!app.shipId && app.shipId !== companionShipId;
  const currentShip = ships?.find((s) => s.id === app.shipId);
  const shipName = currentShip ? currentShip.name : "";
  const companionName = ships?.find((s) => s.id === companionShipId)?.name ?? "";

  const disconnectCompanion = async () => {
    if (!companionShipId || cleanupPending) {
      return;
    }
    setDisconnecting(true);
    setDisconnectFailed(false);
    invalidateCompanionConnect(companionShipId);
    try {
      await ShipSelectViewData.cancelLocal(app.apiClient, companionShipId);
      Session.clearCompanion(companionShipId);
      if (app.shipId === companionShipId) {
        await app.setShipId(companionShipId, true, true);
      }
      setDisconnected(true);
    } catch (error) {
      logger.error("companion disconnect failed", error);
      setDisconnectFailed(true);
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <BlockContainer className={`ShipContentView-empty-select  main-page-containers ${app.cloudSession() ? "cloud-mode" : "app-mode"}`}>

      {/* Overlay No Internet */}
      {/* {!app.internetConnection () && */}
      {/* eslint-disable-next-line no-constant-binary-expression -- intentionally disabled overlay, kept for future re-enablement */}
      {false && (
        <Overlay closeCallback={() => resetAppOverlay()} className="">
          <div>
            <OverlayTitle style="alarm" title={t("alert_no_internet_title")} closeCallback={cancelEdit} />
            <div className="Overlay-body">
              <h3>{t("alert_no_internet_text")}</h3>
            </div>
          </div>
        </Overlay>
      )}
      {/* } */}

      {!app.cloudSession() && (
        <>
          {/* 1st column: a ship is opened */}
          {companionShipId && (
            <Block className="current-ship-block">
              <BlockRow>
                <BlockField className="error-message">{t("companion_reconnect_notice")}</BlockField>
              </BlockRow>
              <BlockRow>
                <BlockField>{companionName}</BlockField>
              </BlockRow>
              {cleanupFailed && (
                <BlockRow>
                  <BlockField className="error-message companion-disconnect-error">{connectT("connect_logbook_failed", { name: companionName })}</BlockField>
                </BlockRow>
              )}
              <BlockRow>
                <BlockField>
                  <button className="button default ship-select-button" disabled={cleanupPending} onClick={() => setAppOverlay(AppOverlayContent.shipOpen, { shipId: companionShipId, nearby: nearbyShipIds?.includes(companionShipId) === true })}>
                    {connectT("connect_logbook")}
                  </button>
                </BlockField>
              </BlockRow>
              <BlockRow>
                <BlockField>
                  <button className="button ship-select-button companion-disconnect" disabled={cleanupPending} onClick={disconnectCompanion}>
                    {t("ship_close_companion", { name: companionName })}
                  </button>
                </BlockField>
              </BlockRow>
            </Block>
          )}
          {shipSelected && (
            <Block className="current-ship-block">
              <BlockRow>
                <h3>{t("current_ship", { name: shipName })}</h3>
              </BlockRow>
              <BlockRow className="block-row-top0">
                <BlockField>
                  <button className="button default ship-select-button" onClick={() => {
                    navigateApp(AppRoutes.ship, { shipId: app.shipId! });
                  }}
                  >{t("ship_logbook", { name: shipName.replace(/ /g, "\u00A0") })}</button>
                </BlockField>
              </BlockRow>
              {app.primarySession() && (
                <BlockRow>
                  <BlockField>
                    <button className="button default ship-select-button" onClick={() => setAppOverlay(AppOverlayContent.closeShip, { shipId: app.shipId! })}>
                      {t("ship_close_confirm", { name: shipName })}
                    </button>
                  </BlockField>
                </BlockRow>
              )}
              {app.localSession() && (
                <BlockRow>
                  <BlockField>
                    <button className="button default ship-select-button" onClick={() => app.logout()}>
                      {t("ship_close_companion", { name: shipName })}
                    </button>
                  </BlockField>
                </BlockRow>
              )}
            </Block>
          )}

          {/* 1st column: no ship opened */}
          {!shipSelected && !companionShipId && (
            <Block>
              <BlockRow>
                <h3>{t("no_current_ship")}</h3>
              </BlockRow>
            </Block>
          )}
        </>
      )}

      {/* 2nd column */}
      {!app.localSession() && (
        <>
          <Block className="ship-select-block">
            <BlockRow>
              <h3>{t("select_ship")}</h3>
            </BlockRow>

            {/* Why every row below is disabled. Stated once for the whole list rather
                than repeated under each ship: it is one fact about this device, not a
                property of any individual ship (#1279). */}
            {ownShipId != undefined && (
              <BlockRow className="ship-select-blocked-notice-row">
                <BlockField className="ship-select-blocked-notice">{t("ship_open_here_notice")}</BlockField>
              </BlockRow>
            )}

            {/* The companion pairing stored on this device could not be reached, so the
                user is here rather than in their logbook. Says so, and what to do about
                it -- connecting again re-resolves the primary's current address. */}
            {companionReconnect && !companionShipId && !disconnected && (
              <BlockRow>
                <BlockField className="ship-select-blocked-notice error-message companion-reconnect-notice">{t("companion_reconnect_notice")}</BlockField>
              </BlockRow>
            )}

            {accountShips ? <ShipSelectView nearbyShipIds={nearbyShipIds} ownShipId={ownShipId} ships={accountShips.filter((s) => s.id !== companionShipId)} users={membership} /> : <Loading />}

            {sharedShips && sharedShips.length > 0 && app.hasFeature("multi-user") && (
              <>
                <BlockRow>
                  <h3>{t("select_shared_ship")}</h3>
                </BlockRow>
                <ShipSelectView nearbyShipIds={nearbyShipIds} ships={sharedShips} viewable={false} />
              </>
            )}
          </Block>

          {/* 3rd column */}
          <Block className="new-ship-block">
            {user.accountId != undefined && (app.cloudSession() || app.hasFeature("primary")) && (
              <>
                <BlockRow>
                  <h3>{t("new_ship")}</h3>
                </BlockRow>

                <BlockRow className="block-row-top0">
                  <BlockField>
                    <button className="button default ship-select-button" onClick={() => app.cloudSession() ? navigateApp(AppRoutes.newShip) : app.openServer("ships/new")} disabled={shipSelected}>
                      {t("add_new_ship")}
                    </button>
                  </BlockField>
                </BlockRow>
                <BlockRow>
                  <BlockField>
                    <button className="button ship-select-button" onClick={() => setAppOverlay(AppOverlayContent.importShip)}>
                      {t("import_ship")}
                    </button>
                  </BlockField>
                </BlockRow>
              </>
            )}
          </Block>
        </>
      )}
    </BlockContainer>
  );
}
